
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Radiology Reports Service
 * Handles all radiology-related data fetching through secure backend APIs
 */

/**
 * Get radiology reports for a patient
 * @param {string} patientId - Patient ID
 * @param {Object} options - Query options
 * @param {Date} options.startDate - Start date for filtering
 * @param {Date} options.endDate - End date for filtering
 * @param {string} options.reportType - Report type filter
 * @param {string} options.reviewStatus - Review status filter
 * @param {number} options.page - Page number for pagination
 * @param {number} options.limit - Number of items per page
 * @returns {Promise<Array>} Promise resolving to array of radiology reports
 */
export async function getRadiologyReports(patientId, options = {}) {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    
    params.append('patientId', patientId);
    if (options.startDate) params.append('startDate', options.startDate.toISOString());
    if (options.endDate) params.append('endDate', options.endDate.toISOString());
    if (options.reportType) params.append('reportType', options.reportType);
    if (options.reviewStatus) params.append('reviewStatus', options.reviewStatus);
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    
    const response = await axios.get(
      `${API_BASE}/clinical/patients/${patientId}/radiology-reports?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.reports || [];
  } catch (error) {
    console.error('Error fetching radiology reports:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

/**
 * Get a specific radiology report by ID
 * @param {string} reportId - Report ID
 * @returns {Promise<Object>} Promise resolving to radiology report
 */
export async function getRadiologyReportById(reportId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/radiology-reports/${reportId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.report;
  } catch (error) {
    console.error('Error fetching radiology report:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

/**
 * Get radiology reports by modality
 * @param {string} patientId - Patient ID
 * @param {string} modality - Imaging modality (e.g., 'CT', 'X-Ray', 'MRI')
 * @param {Object} options - Additional options
 * @returns {Promise<Array>} Promise resolving to array of radiology reports
 */
export async function getRadiologyReportsByModality(patientId, modality, options = {}) {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    
    params.append('patientId', patientId);
    params.append('modality', modality);
    if (options.startDate) params.append('startDate', options.startDate.toISOString());
    if (options.endDate) params.append('endDate', options.endDate.toISOString());
    
    const response = await axios.get(
      `${API_BASE}/clinical/patients/${patientId}/radiology-reports/by-modality?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.reports || [];
  } catch (error) {
    console.error('Error fetching radiology reports by modality:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

/**
 * Get radiology reports from encounters
 * @param {string} patientId - Patient ID
 * @param {Object} options - Query options
 * @param {Date} options.startDate - Start date for filtering
 * @param {Date} options.endDate - End date for filtering
 * @returns {Promise<Array>} Promise resolving to array of radiology reports from encounters
 */
export async function getRadiologyReportsFromEncounters(patientId, options = {}) {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    
    params.append('patientId', patientId);
    if (options.startDate) params.append('startDate', options.startDate.toISOString());
    if (options.endDate) params.append('endDate', options.endDate.toISOString());
    
    const response = await axios.get(
      `${API_BASE}/clinical/patients/${patientId}/radiology-reports/from-encounters?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.reports || [];
  } catch (error) {
    console.error('Error fetching radiology reports from encounters:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}
